import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";
import ScheduleEditor from "../core/ScheduleEditor";
import MessageThread from "./MessageThread";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function tsToLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function dateLabel(dateStr) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Shared by both the vendor-detail view (FacilityAdminDashboard.jsx's
// Vendors tab) and the staff-detail view (Internal Staff tab, and
// VendorAdminDashboard.jsx's own "Your Crew") — Phase 13. Same shape
// either way, just scoped by vendorId vs. workerId: a real activity feed
// (reusing the same checklistCompletions/timeCardEntries + photo
// rendering shape ReportPanel.jsx already uses for its generated report,
// just scoped to one entity instead of a date range), an "On site now"
// badge derived from an open timeCardEntries doc rather than a separate
// "project" concept the app has no other trace of, and the recurring
// schedule (Phase 13's genuinely new feature, see ScheduleEditor.jsx).
//
// The Message button/thread only appears for a Facility Admin viewer —
// message threads are strictly a two-party admin↔vendor or admin↔worker
// conversation (see firestore.rules' Phase 12 comment); a Vendor Admin
// viewing their own crew member here has no defined role in that
// conversation, so this deliberately doesn't offer a third voice into it.
//
// `vendorId` (optional): only meaningful when scopeType==="worker" and the
// viewer is that worker's own Vendor Admin (VendorAdminDashboard.jsx's
// "Your Crew" click-through) — see the query-shape comment below for why
// it's needed.
export default function EntityDetailPanel({ facilityId, scopeType, scopeId, vendorId, label, sub, viewerRole, viewerName, children }) {
  const [completions, setCompletions] = useState([]);
  const [timeCards, setTimeCards] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [showMessages, setShowMessages] = useState(false);
  // Feed used to be a flat "most recent 30" cap regardless of how dense
  // an entity's activity is — for a busy vendor that's still a lot of
  // scrolling on any given day. A "since" date lets the viewer narrow it
  // themselves instead. Defaults to 30 days back, not all-time, so a
  // long-tenured vendor doesn't load their entire history by default.
  const [sinceDate, setSinceDate] = useState(daysAgoStr(30));

  const idField = scopeType === "vendor" ? "vendorId" : "workerId";

  // firestore.rules' `list` rules for checklistCompletions/timeCardEntries
  // only authorize via isFacilityAdmin(resource.data.facilityId) or
  // isVendorAdminByVendorId(resource.data.vendorId) — Firestore can only
  // prove those branches when the *matching field* is itself one of the
  // query's `where` filters (the same list-query-provability constraint
  // documented at length on vendorContracts above). Filtering by
  // idField (vendorId/workerId) alone, as this used to, satisfies
  // neither branch when idField is "workerId" — so facilityId is always
  // included here (makes the Facility Admin branch provable for both
  // vendor and worker views), and vendorId is added too when the caller
  // knows it (makes the Vendor Admin branch provable for their own crew).
  useEffect(() => {
    const constraints = [where("facilityId", "==", facilityId), where(idField, "==", scopeId)];
    if (scopeType === "worker" && vendorId) constraints.push(where("vendorId", "==", vendorId));
    return onSnapshot(query(collection(db, "checklistCompletions"), ...constraints), s => setCompletions(s.docs.map(d => d.data())));
  }, [facilityId, idField, scopeId, scopeType, vendorId]);

  useEffect(() => {
    const constraints = [where("facilityId", "==", facilityId), where(idField, "==", scopeId)];
    if (scopeType === "worker" && vendorId) constraints.push(where("vendorId", "==", vendorId));
    return onSnapshot(query(collection(db, "timeCardEntries"), ...constraints), s => setTimeCards(s.docs.map(d => d.data())));
  }, [facilityId, idField, scopeId, scopeType, vendorId]);

  useEffect(() => onSnapshot(query(collection(db, "schedules"), where("facilityId", "==", facilityId), where("scopeType", "==", scopeType), where("scopeId", "==", scopeId)), s =>
    setSchedules(s.docs.map(d => ({ id: d.id, ...d.data() })))), [facilityId, scopeType, scopeId]);

  const onSiteNow = timeCards.some(t => t.status === "open");

  const sinceMs = sinceDate ? new Date(`${sinceDate}T00:00:00`).getTime() : 0;
  const feed = [
    ...completions.map(c => ({
      kind: "Checklist", ts: c.timestamp,
      detail: `${c.items?.filter(i => i.done).length ?? 0}/${c.items?.length ?? 0} items completed`,
      photos: [...(c.photoUrls?.before || []), ...(c.photoUrls?.after || [])],
    })),
    ...timeCards.map(t => ({
      kind: "Time Card", ts: t.clockInAt,
      detail: t.status === "open" ? "Clocked in, not yet clocked out" : "Clocked in → out",
      photos: [t.clockInPhotoUrl, t.clockOutPhotoUrl].filter(Boolean),
    })),
  ].filter(e => (e.ts?.seconds || 0) * 1000 >= sinceMs)
    .sort((a, b) => (b.ts?.seconds || 0) - (a.ts?.seconds || 0));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{label}</div>
            {onSiteNow && <span style={S.pill(C.tintSuccess, C.success)}>🟢 On site now</span>}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>{sub}</div>
        </div>
        {viewerRole === "facility_admin" && (
          <button style={{ ...S.ghostBtn, width: "auto", padding: "8px 14px" }} onClick={() => setShowMessages(s => !s)}>
            {showMessages ? "Hide Messages" : "Message"}
          </button>
        )}
      </div>

      {showMessages && viewerRole === "facility_admin" && (
        <div style={{ marginBottom: 20, background: C.white, border: `1px solid ${C.border}`, borderRadius: 0, padding: 16, minHeight: 380 }}>
          <MessageThread facilityId={facilityId} threadType={scopeType} threadId={scopeId} senderRole="facility_admin" senderName={viewerName || "Facility Admin"} />
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <div style={{ ...S.kicker, marginBottom: 8 }}>Schedule</div>
        {(viewerRole === "facility_admin" || viewerRole === "vendor_admin") ? (
          <ScheduleEditor facilityId={facilityId} scopeType={scopeType} scopeId={scopeId} vendorId={vendorId} schedules={schedules} viewerRole={viewerRole} />
        ) : schedules.length === 0 ? (
          <div style={{ fontSize: 13, color: C.muted }}>No schedule set.</div>
        ) : (
          schedules.map(s => (
            <div key={s.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
              {s.label && <b>{s.label} — </b>}
              {s.date ? dateLabel(s.date) : s.daysOfWeek.map(d => DAY_LABELS[d]).join(", ")}, {s.startTime}–{s.endTime}
              {s.date && <span style={{ ...S.pill(C.tintAccent, C.accent), marginLeft: 8 }}>One-time</span>}
            </div>
          ))
        )}
      </div>

      {children}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <div style={{ ...S.kicker }}>Activity</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 12, color: C.muted }}>Since</label>
          <input type="date" style={{ ...S.input, marginBottom: 0, padding: "6px 10px", width: "auto", fontSize: 12 }} value={sinceDate} onChange={e => setSinceDate(e.target.value)} />
          {sinceDate && <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 10px", fontSize: 12 }} onClick={() => setSinceDate("")}>Show all</button>}
        </div>
      </div>
      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {feed.length === 0 && <div style={{ padding: 20, fontSize: 13, color: C.muted, textAlign: "center" }}>{sinceDate ? `No activity since ${dateLabel(sinceDate)}.` : "No activity yet."}</div>}
        {feed.map((e, idx) => (
          <div key={idx} style={{ padding: "12px 16px", borderBottom: idx === feed.length - 1 ? "none" : `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <div><span style={S.pill(C.tintAccent, C.accent)}>{e.kind}</span> {e.detail}</div>
              <div style={{ color: C.muted }}>{tsToLabel(e.ts)}</div>
            </div>
            {e.photos.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {e.photos.map((url, i) => <img key={i} src={url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 0, border: `1px solid ${C.border}` }} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
